/**
 * **The selection, painted over the plot** — KANON `pomiary-klienta-2026-09-18`, direction D.
 *
 * A chart CHILD rather than a wrapper, because everything it draws is in the plot's own
 * coordinates: it reads `xScale` and `yScale` out of the chart's context, which is the only place
 * a threshold, an instant or a value can be turned into a pixel and re-turned when bklit tweens
 * the domain. `__isPostOverlay` puts it above the series and outside the reveal clip, which is
 * where an overlay belongs — see `shared/vendor/bklit/chart-child-passthrough.ts`.
 *
 * ── Four layers, in this order, and the order is the argument ────────────────────────────────
 * 1. The TREND line, when the tab is drawing one. With Trend on, `ReadoutChart` draws the raw
 *    readings as dots alone and the line here is the centred moving average — so one bad morning
 *    is a dot off the line rather than a cliff in it.
 * 2. The VEIL, over everything outside A..B. It is the card's own ground at 62%, so the stretch
 *    the trainer is not asking about recedes without being hidden.
 * 3. The SELECTION, stroked in the ember gradient over an ember wash and clipped to A..B.
 * 4. The PROJECTION, dashed, with its end value labelled.
 *
 * ── The ember marks a SELECTION, not a series ────────────────────────────────────────────────
 * `shared/vendor/AGENTS.md` otherwise reads as a flat ban on the accent inside a plot, and it is
 * right: a series wearing ember spends the one colour that still means "act on this". A stretch
 * the trainer just dragged two handles around is a selection, and the owner asked for it by name
 * on 18 September 2026 because the first draft was "zbyt czarno-białe i mało czytelne". No glow —
 * every paint here is a fill or a stroke, and there is no shadow, blur or filter anywhere in it.
 *
 * The gradient's three stops are `--accent-grad-from|mid|to`, read through CSS-module classes with
 * `stopColor="currentColor"`: Stylelint forbids the `--ember-*` ramp in a component stylesheet and
 * a hex in this file would be worse. The precedent is `pages/client-roster`'s `RosterTable`.
 */

import { curveMonotoneX } from '@visx/curve';
import { useId } from 'react';

import { seriesPathFromPoints, useChartStable } from '~/shared/ui/chart/chartOverlay';

import type { MeasurementPoint } from '../model/measurements';
import type { TwoDatesProjection, TwoDatesSelection } from '../model/twoDates';

import styles from './TwoDates.module.css';

/** The ember stroke's weight, against the 1.6px the neutral series is drawn at. */
const SELECTION_STROKE = 3;
/** The neutral trend line's weight. Heavier than a raw series, because it is the reading. */
const TREND_STROKE = 2;
/** The wash under the selected stretch. A wash, never a glow. */
const WASH_TOP_OPACITY = 0.26;
/** How far the clip and the veil reach past the plot, so a stroke's cap is never sliced. */
const BLEED = 8;

export interface TwoDatesOverlayProps {
  /** The line to derive every path from: the trend when there is one, the raw readings otherwise. */
  readonly line: readonly MeasurementPoint[];
  /** Whether this overlay strokes the neutral line itself — true exactly when Trend is on. */
  readonly strokeLine: boolean;
  readonly selection: TwoDatesSelection | null;
  readonly projection: TwoDatesProjection | null;
  readonly reducedMotion: boolean;
}

export function TwoDatesOverlay({
  line,
  strokeLine,
  selection,
  projection,
  reducedMotion,
}: TwoDatesOverlayProps) {
  const { xScale, yScale, innerWidth, innerHeight } = useChartStable();
  // React's own id punctuation is not safe inside a `url(#…)` reference.
  const paintId = `two-dates-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const clipId = `${paintId}-clip`;
  const strokeId = `${paintId}-stroke`;
  const washId = `${paintId}-wash`;

  if (innerWidth <= 0 || innerHeight <= 0) return null;

  const at = (point: MeasurementPoint, index: number) => ({
    x: xScale(point.x) ?? 0,
    y: yScale(point.y) ?? 0,
    key: String(index),
    defined: Number.isFinite(xScale(point.x)) && Number.isFinite(yScale(point.y)),
  });

  const points = line.map(at);
  // The same curve bklit's own `Area` draws with, so the selection's stroke lies exactly on the
  // line under it rather than beside it.
  const path = seriesPathFromPoints(points, curveMonotoneX);
  const first = points[0];
  const last = points[points.length - 1];
  const areaPath =
    path === '' || first === undefined || last === undefined
      ? ''
      : `${path}L${String(last.x)},${String(innerHeight)}L${String(first.x)},${String(innerHeight)}Z`;

  const selectionFrom = selection === null ? null : (xScale(new Date(selection.a)) ?? 0);
  const selectionTo = selection === null ? null : (xScale(new Date(selection.b)) ?? 0);
  const hasSelection =
    selectionFrom !== null && selectionTo !== null && selectionTo > selectionFrom;

  const projectionPath =
    projection === null
      ? ''
      : `M${String(xScale(projection.from.x) ?? 0)},${String(yScale(projection.from.y) ?? 0)}L${String(
          xScale(projection.to.x) ?? 0,
        )},${String(yScale(projection.to.y) ?? 0)}`;

  return (
    <g className={reducedMotion ? undefined : styles.overlayIn}>
      <defs>
        {hasSelection ? (
          <clipPath id={clipId}>
            <rect
              height={innerHeight + 2 * BLEED}
              width={(selectionTo ?? 0) - (selectionFrom ?? 0)}
              x={selectionFrom ?? 0}
              y={-BLEED}
            />
          </clipPath>
        ) : null}
        {/* Horizontal, in the plot's own user space, so the three hues run across the SELECTION
          * rather than across whatever bounding box a path happens to have. */}
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={strokeId}
          x1={selectionFrom ?? 0}
          x2={selectionTo ?? 0}
          y1={0}
          y2={0}
        >
          <stop className={styles.emberFrom} offset="0" stopColor="currentColor" />
          <stop className={styles.emberMid} offset="0.52" stopColor="currentColor" />
          <stop className={styles.emberTo} offset="1" stopColor="currentColor" />
        </linearGradient>
        <linearGradient id={washId} x1="0" x2="0" y1="0" y2="1">
          <stop
            className={styles.emberWash}
            offset="0"
            stopColor="currentColor"
            stopOpacity={WASH_TOP_OPACITY}
          />
          <stop className={styles.emberWash} offset="1" stopColor="currentColor" stopOpacity={0} />
        </linearGradient>
      </defs>

      {strokeLine && path !== '' ? (
        <path className={styles.trendLine} d={path} strokeWidth={TREND_STROKE} />
      ) : null}

      {hasSelection ? (
        <>
          {/* Outside the selection the line recedes. The card's own ground, so nothing is tinted. */}
          <rect
            className={styles.veil}
            height={innerHeight}
            width={Math.max(selectionFrom ?? 0, 0)}
            x={0}
            y={0}
          />
          <rect
            className={styles.veil}
            height={innerHeight}
            width={Math.max(innerWidth - (selectionTo ?? 0), 0)}
            x={selectionTo ?? 0}
            y={0}
          />
          <g clipPath={`url(#${clipId})`}>
            {areaPath === '' ? null : <path d={areaPath} fill={`url(#${washId})`} />}
            <path
              className={styles.selectionStroke}
              d={path}
              stroke={`url(#${strokeId})`}
              strokeWidth={SELECTION_STROKE}
            />
          </g>
        </>
      ) : null}

      {projection === null ? null : (
        <>
          <path className={styles.projectionLine} d={projectionPath} />
          <text
            className={styles.projectionLabel}
            x={(xScale(projection.to.x) ?? 0) + 8}
            y={(yScale(projection.to.y) ?? 0) + 4}
          >
            {projection.label}
          </text>
        </>
      )}
    </g>
  );
}

/**
 * bklit classifies every chart child by its component name, and this one belongs ABOVE the series
 * and OUTSIDE the reveal clip — the series sweeps in, the selection over it does not sweep with it.
 * `displayName` survives minification where a function name does not.
 */
TwoDatesOverlay.displayName = 'TwoDatesOverlay';
(TwoDatesOverlay as { __isPostOverlay?: boolean }).__isPostOverlay = true;
